(function () {
  setupDarkMode();
  setupMobileMenu();
  setupPostTransition();
  setupSiteCreditMenu();
  setupArchetypes();
  setupPostAlternatingCaptionAlignments();
  setupNavVisibility();
  setupHeroSlideshow();
  setupCopyToClipboardButtons();
  setupArchetypeTextColors();
  setupLightbox('.kg-image-card > .kg-image[width][height], .kg-gallery-image > img');
})();

// Setup functions
// ---------------------------------------------------------------------------------

// Dark mode
function setupDarkMode() {
  // if on theme saved in local storage, initialize local storage theme value to user's
  // device-level theme preference. if user has no device-level theme prefence, set it
  // to light theme by default.
  if (!localStorage.getItem('theme')) {
    const userPrefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    localStorage.setItem("theme", userPrefersDark ? "dark" : "light");
  }

  const isDarkMode = () => 'dark' === document.querySelector("html").getAttribute("data-theme");
  const turnOnDarkMode = () => {
    localStorage.setItem('theme', 'dark');
    document.querySelector("html").setAttribute("data-theme", "dark")
  };
  const turnOffDarkMode = () => {
    localStorage.setItem('theme', 'light');
    document.querySelector("html").setAttribute("data-theme", "light")
  };
  const toggleDarkMode = () => isDarkMode() ? turnOffDarkMode() : turnOnDarkMode();

  // initialize dark mode
  if ('dark' === localStorage.getItem('theme')) {
    turnOnDarkMode();
  } else {
    turnOffDarkMode();
  }

  // bind dark mode button to click handlers
  document.querySelectorAll('.dark-mode-toggler').forEach((el) => el.addEventListener('click', () => toggleDarkMode()));
}

// Mobile menu
function setupMobileMenu() {
  document.querySelectorAll('.menu-toggler').forEach((el) => el.addEventListener('click', () => toggleMenu()));

  // Close mobile when resizing window from mobile to desktop breakpoint to ensure page is not inert
  window.addEventListener('resize', () => {
    const style = getComputedStyle(document.documentElement);
    const mobileMenuBreakpoint = parseInt(style.getPropertyValue('--breakpoint-mm'));

    if (window.innerWidth >= mobileMenuBreakpoint) {
      document.querySelector("#mobile-menu").classList.remove("open");
    }
  });

  // Handle page inert and overflow according to menu's open state
  const observer = new MutationObserver(function(mutationsList) {
    const isOpen = mutationsList[0].target.classList.contains("open");

    if (isOpen) {
      document.querySelectorAll("main,footer").forEach((el) => el.setAttribute("inert", true));
      document.querySelector("body").style.overflow = "hidden";
    } else {
      document.querySelectorAll("main,footer").forEach((el) => el.removeAttribute("inert"));
      document.querySelector("body").style.overflow = "auto";
    }
  });

  observer.observe(document.querySelector("#mobile-menu"), {
    attributes: true,
    attributeFilter: ["class"]
  });

  // When links are clicked, close menu; This ensures that anchor links to the same page as
  // the current location close the menu, despite the page not changing
  document.querySelectorAll("#mobile-menu a").forEach((el) => el.addEventListener("click", function(e) {
    const linkIsToThisPage = window.location.pathname === new URL(e.target.href).pathname;
    
    if (linkIsToThisPage) {
      closeMenu();
    }
  }));

  // Toggle mobile menu visibility
  function toggleMenu(open) {
    const isOpen = document.querySelector("#mobile-menu").classList.toggle("open");
    document.querySelector(".menu-toggler").classList.toggle("open");
  }
  function closeMenu() {
    document.querySelector("#mobile-menu").classList.remove("open");
    document.querySelector(".menu-toggler").classList.remove("open");
  }
}

// Most of the post entry animation happens with just HTML and CSS, but here we add the 'entered' class to viewfinder
// dots after the animation has finished fading out so that we can force the dots to keep the accent color even when
// the user toggles dark mode on and off. Without this, the dot color would re-animate each time that's toggled.
function setupPostTransition() {
  setTimeout(() => {
    document.querySelectorAll('.dot-wrap').forEach(el => el.classList.add("entered"));
  }, 1650);
}

// Site credit menu (in footer)
function setupSiteCreditMenu() {
  const menu = document.querySelector("#side-credit-menu");
  const toggler = document.querySelectorAll(".site-credit-menu-toggler");
  toggler.forEach((el) => el.addEventListener("click", () => menu.classList.toggle("hidden")));
}

// Archetype expand/collapse functionality
function setupArchetypes() {  
  function expandArchetype(archetypeElement, withScroll = true) {
    if (!archetypeElement) return;

    if (withScroll) {
      setTimeout(() => {
        const currentScrollY = window.scrollY || window.pageYOffset; // Get current scroll position
        const offset = document.querySelector('.volume-header').scrollHeight;
        window.scrollTo({
          top: archetypeElement.offsetTop - offset,
          behavior: archetypeElement.offsetTop + offset > currentScrollY ? 'smooth' : 'instant' // If target archetype is below, smooth scroll, otherwise instant
        });
      });
    }
  
    archetypeElement.classList.add("expanded");
  }

  function collapseArchetype(archetypeElement) {
    if (!archetypeElement) return;
    archetypeElement.classList.remove("expanded");
  }

  function toggleArchetype(archetypeElement) {
    if (!archetypeElement) return;

    if (archetypeElement.classList.contains("expanded")) {
      collapseArchetype(archetypeElement);
    } else {
      expandArchetype(archetypeElement);
    }
  }

  function expandAllArchetypesInVolume(volumeElement) {
    if (!volumeElement) return;

    const archetypes = volumeElement.querySelectorAll(".archetype-wrap");

    if (archetypes?.length <= 0) return;

    // Expand all Archetypes, only setting withScroll to true for the first in the volume
    archetypes.forEach((el, i) => expandArchetype(el, i === 0));
  }

  function collapseAllArchetypesInVolume(volumeElement) {
    if (!volumeElement) return;

    const archetypes = volumeElement.querySelectorAll(".archetype-wrap");

    if (archetypes?.length <= 0) return;

    // Expand all Archetypes, only setting withScroll to true for the first in the volume
    archetypes.forEach((el, i) => collapseArchetype(el));
  }
  document.querySelectorAll('.archetype-toggler')?.forEach((el) => el.addEventListener('click', (e) => {
    toggleArchetype(e.target.closest(".archetype-wrap"));
  }));

  document.querySelectorAll('.volume-expand-all-toggler')?.forEach((el) => el.addEventListener('click', (e) => {
    if (e.target.innerHTML.includes("Expand")) {
      expandAllArchetypesInVolume(e.target.closest(".volume-wrap"));
    } else {
      collapseAllArchetypesInVolume(e.target.closest(".volume-wrap"))
    }
  }));

  // Mutation observers for Volume state (Expand all vs. Collapse all based on whether a contained Archetype is expanded)
  const observer = new MutationObserver(function(mutationsList) {
    const relevantMutations = mutationsList.filter((mutation) => (
      mutation.type === "attributes",
      mutation.attributeName === "class"
    ));

    if (relevantMutations.length <= 0) return;

    const volume = relevantMutations[0].target.closest(".volume-wrap");
    const expandAllToggler = volume.querySelector(".volume-expand-all-toggler");
    const volumeContainsExpandedArchetype = volume.querySelectorAll(".archetype-wrap.expanded").length > 0;

    expandAllToggler.innerHTML = (volumeContainsExpandedArchetype) ? "Collapse All -" : "Expand All +";
  });

  document.querySelectorAll(".volume-wrap").forEach(volumeElement => {
    observer.observe(volumeElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"]
    });
  });

  // On load, scroll to target Archetype if there's one specified in URL hash
  if (window.location.pathname.includes("archetypes") && window.location.hash) {
    const hash = window.location.hash;

    // If volume, jump to Archetype marked with both volume and Archetype
    // This happens when user clicks tag within a post or copies a share link
    // We do this check first to support the future possibility of multiple instances
    // of an archetype name across volumes.
    if (hash.includes("volume")) {
      const volume = hash.split("-")[1];
      const archetype = hash.split(`volume-${volume}-`)[1];
      const selector = `.archetype-wrap[data-volume='${volume}'][data-archetype='${archetype}'] .archetype-toggler`;
      const scrollTarget = document.querySelector(selector);
      setTimeout(() => scrollTarget.click());
    } else {
      // As a backup, if no volume is specified in the hash, go to the first archetype tagged
      // with the hash value. This Can happen when use navigates to the /archetypes page from the
      // built-in Ghost search bar.
      const archetype = hash.split("#")[1].replaceAll("/", "");
      const selector = `.archetype-wrap[data-archetype='${archetype}'] .archetype-toggler`;
      const scrollTarget = document.querySelector(selector);
      setTimeout(() => scrollTarget.click());
    }
  }
}

// Show/hide top nav depending on scroll direction
function setupNavVisibility() {
  handleNavVisibilityOnScroll(document.querySelector("nav#desktop-menu"));
  handleNavVisibilityOnScroll(document.querySelector("nav#mobile-menu"));

  function handleNavVisibilityOnScroll(navElement) {
    let lastScrollTop = 0;
    let buffer = 2;

    window.addEventListener('scroll', function() {
      const navHeight = navElement.getBoundingClientRect().height;
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (currentScrollTop <= navHeight) return;
      
      const direction = (currentScrollTop > lastScrollTop)
        ? "down"
        : "up";

      if ("down" === direction) {
        navElement.classList.add("hide");

        document.querySelectorAll(".volume-header")?.forEach(el => {
          el.classList.remove("nav-visible");
          el.style.top = 0;
        });
      } else if ("up" === direction) {
        navElement.classList.remove("hide");

        document.querySelectorAll(".volume-header")?.forEach(el => {
          el.classList.add("nav-visible");
          if (navHeight > 0) {
            el.style.top = `${navHeight}px`;
          }
        });
      }

      lastScrollTop = "down" === direction
        ? currentScrollTop - buffer
        : currentScrollTop + buffer;
    });
  }
}

// Add slider buttons to hero component if there are multiple hero slides specified in theme settings
function setupHeroSlideshow() {
  document.querySelectorAll(".hero-slideshow-wrap").forEach((el) => {
    const slideshow = el.querySelectorAll(".hero-slideshow")[0];
    const slides = el.querySelectorAll(".hero-slide");
    const breadcrumbs = el.querySelectorAll(".breadcrumb");

    if (!breadcrumbs || breadcrumbs.length <= 0) {
      return;
    }

    const markActiveBreadcrumb = () => {
      const buffer = window.innerWidth / 2;

      // for each slide
      slides.forEach((slide, i) => {
        // get slide offset left
        const offsetLeft = slide.getBoundingClientRect().left;
        const isActive = offsetLeft >= (-1 * buffer) && offsetLeft <= buffer;

        // if slide left scroll offset is between the buffer values,
        if (isActive && !breadcrumbs[i].classList.contains("active")) {
          //  -> mark all slides as ianctive
          breadcrumbs.forEach((el) => el.classList.remove("active"));
          //  -> mark this slide as active
          breadcrumbs[i].classList.add("active");
        }
      });
    };

    // Scroll to slide with the specified index
    function goToSlide(i, instant = false) {
      const slide = slides[i];
      const scrollLeftBy = slide.getBoundingClientRect().left;
      slideshow.scrollBy({ left: scrollLeftBy, behavior: instant ? 'instant' : 'smooth' });
    }

    // Mark active breadcrumb on page load
    markActiveBreadcrumb();

    // On slideshow scroll, mark breadcrumb as active for the predominantly visible slide
    slideshow.addEventListener("scroll", markActiveBreadcrumb);

    // On breadcrumb click, scroll slideshow conatiner to the corresponding slide
    el.querySelectorAll(".breadcrumb").forEach((el) => {
      el.addEventListener("click", function(event) {
        const index = event.target.getAttribute("data-index");
        if (index) goToSlide(index);
      });
    });

    // On window resize, get the active slide and scroll to it; Otherwise, scroll anchor moves on resize
    window.addEventListener("resize", function() {
      const active = Array.from(breadcrumbs || []).find(el => el.classList.contains("active"));
      const activeIndex = active?.getAttribute("data-index");
      goToSlide(activeIndex, true);
    });
  });
}

// Add class to every other captioned image within a post so we can style alternating alignments
function setupPostAlternatingCaptionAlignments() {
  const imagesWithCaptions = document.querySelectorAll(".kg-image-card");
  
  if (!imagesWithCaptions || imagesWithCaptions.length <= 0) return;

  imagesWithCaptions.forEach((el, i) => {
    if (i % 2 === 0) {
      el.classList.add("right-align-caption");
    } else {
      el.classList.add("left-align-caption");
    }
  });
}

// Enable copy to clipboard functionality on links containing 'copy-url' class
function setupCopyToClipboardButtons() {
  document.querySelectorAll('.copy-url-to-clipboard').forEach((el) => el.addEventListener('click', function() {
    const pathname = this.getAttribute('data-pathname');
    const text = pathname ? `${window.location.origin}${pathname}` : window.location.href;
    copyToClipboard(text);
    const cachedText = this.innerText;
    this.innerText = "Copied ✓";
    setTimeout(() => this.innerText = cachedText, 2150);
  }));
}

// Calculate text color for text that is positioned directly on Archetype background colors
// Assumes hexadecimal color format
function setupArchetypeTextColors() {
  const selectors = ".post-card-tag-mobile, .archetype-wrap";

  document.querySelectorAll(selectors).forEach((el, i) => {
    var accentColor = getComputedStyle(el).getPropertyValue('--accent-color');
    accentColor = accentColor.trim().slice(1);

    if (accentColor.length === 3) {
      accentColor = accentColor[0] + accentColor[0] + accentColor[1] + accentColor[1] + accentColor[2] + accentColor[2];
    }

    var r = parseInt(accentColor.substr(0, 2), 16);
    var g = parseInt(accentColor.substr(2, 2), 16);
    var b = parseInt(accentColor.substr(4, 2), 16);
    var yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    var textColor = (yiq >= 128) ? 'dark' : 'light';

    el.classList.add(("dark" === textColor) ? "text-black" : "text-white");
  });
}

// Enable lightbox functionality on specified elements
function setupLightbox(selector) {
    var onThumbnailsClick = function (e) {
        e.preventDefault();

        var items = [];
        var index = 0;

        var prevSibling = e.target.closest('.kg-card').previousElementSibling;

        while (prevSibling && (prevSibling.classList.contains('kg-image-card') || prevSibling.classList.contains('kg-gallery-card'))) {
            var prevItems = [];

            prevSibling.querySelectorAll('img').forEach(function (item) {
                prevItems.push({
                    src: item.getAttribute('src'),
                    msrc: item.getAttribute('src'),
                    w: item.getAttribute('width'),
                    h: item.getAttribute('height'),
                    el: item,
                })

                index += 1;
            });
            prevSibling = prevSibling.previousElementSibling;

            items = prevItems.concat(items);
        }

        if (e.target.classList.contains('kg-image')) {
            items.push({
                src: e.target.getAttribute('src'),
                msrc: e.target.getAttribute('src'),
                w: e.target.getAttribute('width'),
                h: e.target.getAttribute('height'),
                el: e.target,
            });
        } else {
            var reachedCurrentItem = false;

            e.target.closest('.kg-gallery-card').querySelectorAll('img').forEach(function (item) {
                items.push({
                    src: item.getAttribute('src'),
                    msrc: item.getAttribute('src'),
                    w: item.getAttribute('width'),
                    h: item.getAttribute('height'),
                    el: item,
                });

                if (!reachedCurrentItem && item !== e.target) {
                    index += 1;
                } else {
                    reachedCurrentItem = true;
                }
            });
        }

        var nextSibling = e.target.closest('.kg-card').nextElementSibling;

        while (nextSibling && (nextSibling.classList.contains('kg-image-card') || nextSibling.classList.contains('kg-gallery-card'))) {
            nextSibling.querySelectorAll('img').forEach(function (item) {
                items.push({
                    src: item.getAttribute('src'),
                    msrc: item.getAttribute('src'),
                    w: item.getAttribute('width'),
                    h: item.getAttribute('height'),
                    el: item,
                })
            });
            nextSibling = nextSibling.nextElementSibling;
        }

        var pswpElement = document.querySelectorAll('.pswp')[0];

        var options = {
            bgOpacity: 0.9,
            closeOnScroll: true,
            fullscreenEl: false,
            history: false,
            index: index,
            shareEl: false,
            zoomEl: false,
            getThumbBoundsFn: function(index) {
                var thumbnail = items[index].el,
                    pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
                    rect = thumbnail.getBoundingClientRect();

                return {x:rect.left, y:rect.top + pageYScroll, w:rect.width};
            }
        }

        var gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
        gallery.init();

        return false;
    };

    var elements = document.querySelectorAll(selector);
    elements.forEach(function (el) {
        el.addEventListener('click', function (e) {
            onThumbnailsClick(e);
        });
    });
}




// Helpers
// ---------------------------------------------------------------------------------

function getRelativeScrollPosition() {
  // Get the current scroll position
  const scrolledAmount = window.pageYOffset || document.documentElement.scrollTop;

  // Get the total height of the document (including scrolled-out content)
  const totalDocumentHeight = Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.body.clientHeight,
    document.documentElement.clientHeight
  );

  // Get the viewport height (visible portion of the window)
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  // Calculate the total scrollable height
  const totalScrollableHeight = totalDocumentHeight - viewportHeight;

  // Calculate the scroll percentage
  let scrollPercentage = 0;
  if (totalScrollableHeight > 0) { // Avoid division by zero if content is not scrollable
    scrollPercentage = (scrolledAmount / totalScrollableHeight);
  }

  return { totalScrollableHeight, scrollPercentage };
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
}